import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  CircularProgress,
} from '@mui/material';
import { postRequest } from '../../utils/ApiRequests';
import './candidates.scss';

const OTPVerification = ({ mobileNumber, onVerificationSuccess, onBack, handleSnackBar }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      handleSnackBar(true, 'Please enter a valid 4-digit OTP', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await postRequest('admin/otp/verify', {
        mobile: mobileNumber,
        otp: otp,
      });

      if (response.data.status === 'Success' || response.data.success) {
        handleSnackBar(true, 'OTP verified successfully', 'success');
        onVerificationSuccess();
      } else {
        handleSnackBar(true, response.data.msg || 'Invalid OTP', 'error');
      }
    } catch (err) {
      handleSnackBar(
        true,
        err?.response?.data?.msg || 'OTP verification failed',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    try {
      const response = await postRequest('admin/otp/send', {
        mobile: mobileNumber,
      });

      if (response.data.status === 'Success' || response.data.success) {
        handleSnackBar(true, 'OTP resent successfully', 'success');
        setTimer(30);
        setCanResend(false);
        setOtp('');
      } else {
        handleSnackBar(true, response.data.message || 'Failed to resend OTP', 'error');
      }
    } catch (err) {
      handleSnackBar(
        true,
        err?.response?.data?.message || 'Failed to resend OTP',
        'error'
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 4) {
      setOtp(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otp.length === 4) {
      handleVerifyOTP();
    }
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: '50px' }}>
      <Paper elevation={3} style={{ padding: '40px', borderRadius: '12px' }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h5" gutterBottom fontWeight="600">
            Verify Mobile Number
          </Typography>
          <Typography variant="body2" color="textSecondary">
            We've sent a 4-digit OTP to
          </Typography>
          <Typography variant="body1" fontWeight="600" mt={1}>
            {mobileNumber}
          </Typography>
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            label="Enter OTP"
            variant="outlined"
            value={otp}
            onChange={handleOTPChange}
            onKeyPress={handleKeyPress}
            placeholder="0000"
            inputProps={{
              maxLength: 4,
              style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
            }}
            autoFocus
          />
        </Box>

        <Box mb={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={handleVerifyOTP}
            disabled={loading || otp.length !== 4}
            style={{ height: '50px' }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            variant="text"
            color="secondary"
            onClick={onBack}
            disabled={loading || resendLoading}
          >
            Change Number
          </Button>

          {canResend ? (
            <Button
              variant="text"
              color="primary"
              onClick={handleResendOTP}
              disabled={resendLoading}
            >
              {resendLoading ? <CircularProgress size={20} /> : 'Resend OTP'}
            </Button>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Resend in {timer}s
            </Typography>
          )}
        </Box>

        <Box textAlign="center" mt={3}>
          <Typography variant="caption" color="textSecondary">
            Didn't receive the OTP? Please check your messages or try resending.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default OTPVerification;
